import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { PinInput, PinInputField } from '@chakra-ui/react';
// Custom components
import DefaultAuth from 'layouts/auth/Default';
// Assets
import Chemmanurlogo from 'assets/img/auth/logo.svg';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { useAuth } from '../../../auth-context/auth.context';
import AuthApi from '../../../api/auth';
import CountdownTimer from './countDownTimer';
import InputField from 'components/formComponents/inputField';
import UserApi from 'api/user';
import DashboardReportApi from 'api/dashboardreport';

function SignIn() {
  const [otpValue, setOTPValue] = useState('');
  const [error, setError] = useState(undefined);
  const [buttonText, setButtonText] = useState('Sign in');
  const [showOtp, setShowOTP] = useState(false);
  const [otpID, setotpID] = useState('');
  const history = useHistory();
  const { setUser } = useAuth();
  const { user } = useAuth();
  const toast = useToast();
  const toastIdRef = React.useRef();

  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const textColorBrand = useColorModeValue('brand.500', 'white');

  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .trim()
        .email('Invalid email address')
        .required('Required'),
      password: Yup.string()
        .trim()
        .min(6, 'Password must be at least 6 characters')
        .required('Required'),
    }),
    onSubmit: async (values) => {
      if (user && user.token) {
        return history.push('/admin');
      }
      setButtonText('Signing in');
      try {
        let response = await AuthApi.Login({
          email: values.email,
          password: values.password,
        });

        response = response.data;
        if (response?.status_code === 200 && response.data.otp_id) {
          setShowOTP(true);
          setotpID(response.data.otp_id);
          setError('');
          return;
        }
        if (response?.status_code !== 200) {
          setButtonText('Sign in');
          return setError(response.message || response.data?.message || 'An error occurred during sign in.');
        }
        return setProfile(response);
      } catch (err) {
        console.log(err);
        setButtonText('Sign in');
        if (err.response?.data?.message) {
          return setError(err.response.data.message);
        }
        if (err.message) {
          return setError(err.message);
        }
        return setError('There has been an error.');
      }
    },
  });

  const loginWithOTP = async (event) => {
    if (event) {
      event.preventDefault();
    }
    if (otpValue === '') {
      return setError('You must enter an OTP');
    }

    try {
      let response = await AuthApi.verifyWithOTP({
        otp_id: otpID,
        value: otpValue,
      });

      response = response.data;
      if (response?.status_code !== 200) {
        setButtonText('Sign in');
        return setError(response.message || response.data?.message || 'Invalid OTP');
      }
      return setProfile(response);
    } catch (err) {
      console.log(err);
      setButtonText('Sign in');
      if (err.response?.data?.message) {
        return setError(err.response.data.message);
      }
      if (err.message) {
        return setError(err.message);
      }
      return setError('There has been an error.');
    }
  };

  const setProfile = async (response) => {
    let user = { ...response?.data?.userdetails };
    user.accesstoken = response?.data?.accesstoken;
    user.refreshtoken = response?.data?.refreshtoken;
    user = JSON.stringify(user);
    setUser(user);
    sessionStorage.setItem('user', user);
    sessionStorage.setItem('accesstoken', response?.data?.accesstoken);
    sessionStorage.setItem('refreshtoken', response?.data?.refreshtoken);
    try {
      if (sessionStorage.getItem('permissions')) {
        sessionStorage.removeItem('permissions');
      }
      if (
        response?.data?.userdetails?.user_type === 'ADMIN' ||
        response?.data?.userdetails?.user_type === 'SHOWROOM'
      ) {
        let permissionsResponse = await UserApi.getMyPermissions();
        permissionsResponse = permissionsResponse.data;
        if (permissionsResponse?.status_code === 200) {
          sessionStorage.setItem(
            'permissions',
            JSON.stringify(permissionsResponse.data)
          );
        } else {
          console.error(
            'Failed to fetch permissions',
            permissionsResponse.message
          );
        }
      }

      await DashboardReportApi.Notifications();
    } catch (err) {
      console.error('Error fetching permissions', err);
    }
    toastIdRef.current = toast({
      title: response.message || 'Successfully logged in',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
    return history.push('/admin');
  };

  return (
    <DefaultAuth illustrationBackground={Chemmanurlogo} image={Chemmanurlogo}>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w='100%'
        mx={{ base: 'auto', lg: '0px' }}
        me='auto'
        h='100%'
        alignItems='start'
        justifyContent='center'
        mb={{ base: '30px', md: '60px' }}
        px={{ base: '25px', md: '0px' }}
        mt={{ base: '40px', md: '14vh' }}
        flexDirection='column'
      >
        <Box me='auto'>
          <Heading color={textColor} fontSize='36px' mb='10px'>
            Sign In
          </Heading>
          <Text
            mb='36px'
            ms='4px'
            color={textColorSecondary}
            fontWeight='400'
            fontSize='md'
          >
            {showOtp ? 'Sign In using OTP' : 'Sign In with Email & Password'}
          </Text>
        </Box>
        <Flex
          zIndex='2'
          direction='column'
          w={{ base: '100%', md: '420px' }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: 'auto', lg: 'unset' }}
          me='auto'
          mb={{ base: '20px', md: 'auto' }}
        >
          <h4
            style={{
              fontSize: '.9em',
              color: 'red',
              textAlign: 'center',
              fontWeight: 400,
              transition: '.2s all',
            }}
          >
            {error}
          </h4>
          {showOtp ? (
            <FormControl>
              <FormLabel
                display='flex'
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                mb='8px'
              >
                Enter OTP <Text color='red.500'> *</Text>
              </FormLabel>
              <Flex justifyContent='center' gap='32px' mb='25px'>
                <PinInput
                  value={otpValue}
                  onChange={(value) => {
                    setOTPValue(value);
                    setError(undefined);
                  }}
                  otp
                  size='lg'
                  type='number'
                  placeholder='*'
                  variant='outline'
                  focusBorderColor='blue.500'
                  mb='24px'
                  display='flex'
                >
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                  <PinInputField />
                </PinInput>
              </Flex>

              <CountdownTimer seconds={30} otpID={otpID} />
              <Button
                fontSize='sm'
                variant='brand'
                fontWeight='500'
                w='100%'
                h='50'
                mb='24px'
                onClick={loginWithOTP}
              >
                Submit
              </Button>
            </FormControl>
          ) : (
            <form onSubmit={formik.handleSubmit}>
              <FormControl
                isInvalid={formik.touched.email && formik.errors.email}
              >
                <FormLabel
                  display='flex'
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                >
                  Email
                </FormLabel>
                <Input
                  variant='auth'
                  fontSize='sm'
                  ms={{ base: '0px', md: '0px' }}
                  type='email'
                  placeholder='Enter email'
                  fontWeight='500'
                  size='lg'
                  {...formik.getFieldProps('email')}
                />
                {formik.touched.email && formik.errors.email ? (
                  <Text color='red.500' fontSize='sm'>
                    {formik.errors.email}
                  </Text>
                ) : null}
              </FormControl>
              <FormControl
                isInvalid={formik.touched.password && formik.errors.password}
              >
                <FormLabel
                  ms='4px'
                  fontSize='sm'
                  fontWeight='500'
                  color={textColor}
                  display='flex'
                  mt='24px'
                >
                  Password
                </FormLabel>
                <InputGroup size='md'>
                  <Input
                    variant='auth'
                    fontSize='sm'
                    placeholder='*******'
                    size='lg'
                    type={show ? 'text' : 'password'}
                    {...formik.getFieldProps('password')}
                  />
                  <InputRightElement
                    display='flex'
                    alignItems='center'
                    mt='4px'
                  >
                    <Icon
                      color={textColorSecondary}
                      _hover={{ cursor: 'pointer' }}
                      as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                      onClick={handleClick}
                    />
                  </InputRightElement>
                </InputGroup>
                {formik.touched.password && formik.errors.password ? (
                  <Text color='red.500' fontSize='sm' mb='24px'>
                    {formik.errors.password}
                  </Text>
                ) : null}
              </FormControl>
              <Flex justifyContent='space-between' align='center' mb='24px'>
                <NavLink to='/auth/resetpassword'>
                  <Text
                    color={textColorBrand}
                    fontSize='sm'
                    w='124px'
                    fontWeight='500'
                  >
                    Forgot password?
                  </Text>
                </NavLink>
              </Flex>
              <Button
                fontSize='sm'
                variant='brand'
                fontWeight='500'
                w='100%'
                h='50'
                mb='24px'
                type='submit'
              >
                {buttonText}
              </Button>
            </form>
          )}
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
